import { get } from "@ember/object"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookEditCalendar } from "@shared/models/analytics/planbook"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type {
  Planbook,
  PlanbookDateAnnotationDateRange,
  PlanbookDateAnnotationSingle,
} from "@shared/models/planbook"
import Patch from "@shared/models/patch-updater"
import _ from "lodash"
import moment from "moment"
import { findPlanbookDatesOff } from "../../../dates-off-calculator"
import type { Narrative } from "../../create-narrative"
import defaultPlanbookDateActivity from "./default-planbook-date-activity"
import defaultPlanbookDateContext from "./default-planbook-date-context"
import defaultPlanbookDateModels from "./default-planbook-date-models"

interface Payload {
  annotation: PlanbookDateAnnotationSingle | PlanbookDateAnnotationDateRange
  planbook: Planbook
  calledFrom: string
}

export const PLANBOOK_DATE_ANNOTATION_DELETE = {
  name: "PLANBOOK_DATE_ANNOTATION_DELETE",

  params: {
    annotation: "object",
    planbook: "object",
    calledFrom: "string",
  },

  patches(payload: Payload) {
    let newAnnotations = _.reject(
      payload.planbook.attributes.calendar.dateAnnotations,
      (annotation) => {
        return annotation.id === payload.annotation.id
      }
    )
    let allDatesOff = findPlanbookDatesOff(newAnnotations)

    let patch = Patch.create("planbook", payload.planbook.id)
      .pull("attributes.calendar.dateAnnotations", {
        id: payload.annotation.id,
      })
      .set("attributes.calendar.datesOff", allDatesOff)
      .inc("attributes.calendar.version", 1)

    return [patch.patch]
  },

  undoPatches(payload) {
    let newAnnotations = JSON.parse(
      JSON.stringify(payload.planbook.attributes.calendar.dateAnnotations)
    ).concat([JSON.parse(JSON.stringify(payload.annotation))])
    let allDatesOff = findPlanbookDatesOff(newAnnotations)

    let patch = Patch.create("planbook", payload.planbook.id)
      .push("attributes.calendar.dateAnnotations", payload.annotation)
      .set("attributes.calendar.datesOff", allDatesOff)
      .inc("attributes.calendar.version", 1)

    return [patch.patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultPlanbookDateModels(payload, findFn, userId)
    let context = defaultPlanbookDateContext(payload, models)
    let formattedDateString = moment(get(context, "dateString")).format("ddd, MMM. D, YYYY")
    let formattedStartDate = moment(get(context, "startDate")).format("ddd, MMM. D, YYYY")
    let formattedEndDate = moment(get(context, "endDate")).format("ddd, MMM. D, YYYY")
    // @ts-ignore
    let weekdayTitle = get(models, "rotation.attributes.title")
    let newContext = _.assign({}, context, {
      annotationId: payload.annotation.id,
    })

    let titleInnerPhrase =
      payload.annotation.type === "date-annotation-planbook-custom-single"
        ? "A Single Day"
        : payload.annotation.type === "date-annotation-planbook-custom-range"
        ? "Multiple Days"
        : `Every ${weekdayTitle}`

    let title =
      payload.annotation.attributes.isOff === true
        ? `Turned ${titleInnerPhrase} Back On`
        : `Deleted a note on ${titleInnerPhrase}`

    let descriptionInnerPhrase =
      payload.annotation.type === "date-annotation-planbook-custom-single"
        ? `${formattedDateString}`
        : payload.annotation.type === "date-annotation-planbook-custom-range"
        ? `every day from ${formattedStartDate} to ${formattedEndDate}`
        : `every ${weekdayTitle}`

    let description =
      payload.annotation.attributes.isOff === true
        ? `Turned all classes on ${descriptionInnerPhrase} back on`
        : `Deleted a note on ${descriptionInnerPhrase}`

    let analyticsModifier =
      payload.annotation.type === "date-annotation-planbook-custom-single"
        ? "Single"
        : payload.annotation.type === "date-annotation-planbook-custom-range"
        ? "Range"
        : "Default"

    let newEvent: PlanbookEditCalendar = {
      ...createBlankEvent(),
      root_id: payload.planbook.id,
      root_type: "planbook",
      primary_object_type: "planbook",
      primary_object_id_and_ancestor_ids: [payload.planbook.id],
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),
      primary_verb: "edit",
      primary_object_specifier: "calendar",
      adverbial_verb: "removing",
      adverbial_object_type: "annotation",
    }

    return {
      title,
      titleForAnalytics: `Planbook Date - Delete Annotation - ${analyticsModifier}`,
      description,
      context: newContext,
      activity: defaultPlanbookDateActivity(payload, models, "removed"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default PLANBOOK_DATE_ANNOTATION_DELETE
